import { useState } from "react";
import { useCreateNote, useDeleteNote } from "../hooks/useGrowsQuery.js";
import useTimezone from "../hooks/useTimezone.js";
import { formatDateTimeWithRelative } from "../utility/datetime.js";

const EVENT_TYPES = [
  { value: "",                label: "Plain note" },
  { value: "misted",          label: "Misted" },
  { value: "fan_adjusted",    label: "Fan adjusted" },
  { value: "harvested",       label: "Harvested" },
  { value: "contam_spotted",  label: "Contamination spotted" },
  { value: "phase_transition",label: "Phase transition" },
  { value: "photo_taken",     label: "Photo taken" },
  { value: "custom",          label: "Custom event" },
];

const EVENT_STYLES = {
  misted:           { label: "Misted",        bg: "bg-blue-100",   text: "text-blue-700",   icon: "💧" },
  fan_adjusted:     { label: "Fan",           bg: "bg-purple-100", text: "text-purple-700", icon: "🌀" },
  harvested:        { label: "Harvested",     bg: "bg-emerald-100",text: "text-emerald-700",icon: "🧺" },
  contam_spotted:   { label: "Contamination", bg: "bg-red-100",    text: "text-red-700",    icon: "⚠️" },
  phase_transition: { label: "Phase",         bg: "bg-amber-100",  text: "text-amber-700",  icon: "🔄" },
  photo_taken:      { label: "Photo",         bg: "bg-gray-100",   text: "text-gray-600",   icon: "📷" },
  custom:           { label: "Event",         bg: "bg-gray-100",   text: "text-gray-600",   icon: "📌" },
};

function EventBadge({ eventType }) {
  const style = EVENT_STYLES[eventType];
  if (!style) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
      {style.icon} {style.label}
    </span>
  );
}

function NoteItem({ note, growId, tz }) {
  const deleteNote = useDeleteNote(growId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="flex gap-3 group">
      <div className="w-1.5 shrink-0 mt-1.5 rounded-full self-stretch bg-gray-100" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {note.event_type && <EventBadge eventType={note.event_type} />}
            <span className="text-xs text-gray-400">
              {formatDateTimeWithRelative(note.created_at, tz)}
            </span>
          </div>
          {!confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              aria-label="Delete note"
            >
              ✕
            </button>
          )}
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">{note.note}</p>
        {confirmDelete && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">Delete this note?</span>
            <button
              onClick={() => deleteNote.mutate(note.id)}
              disabled={deleteNote.isPending}
              className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
            >
              {deleteNote.isPending ? "Deleting…" : "Yes, delete"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GrowNotesFeed({ growId, notes = [] }) {
  const createNote = useCreateNote(growId);
  const tz = useTimezone();

  const [text, setText] = useState("");
  const [eventType, setEventType] = useState("");
  const [error, setError] = useState(null);

  const sorted = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await createNote.mutateAsync({
        note: trimmed,
        ...(eventType ? { event_type: eventType } : {}),
      });
      setText("");
      setEventType("");
    } catch {
      setError("Failed to save note");
    }
  }

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Notes & Events</h2>

      {/* Add note form */}
      <form onSubmit={handleSubmit} className="mb-5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note…"
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
        />
        <div className="flex items-center gap-2 mt-2">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={createNote.isPending || !text.trim()}
            className="text-xs bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createNote.isPending ? "Saving…" : "Add note"}
          </button>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      </form>

      {/* Feed */}
      {sorted.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6">No notes yet. Add the first one above.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {sorted.map((note) => (
            <NoteItem key={note.id} note={note} growId={growId} tz={tz} />
          ))}
        </div>
      )}
    </div>
  );
}
